import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Alert, Autocomplete, Button, Col, FormControl, Paper, Radio, RadioGroup, Row, TextField, Typography } from 'components/AORedesign';
import { useClaims } from 'hooks/useClaims';
import { POST_CLAIMS_AUTOCOMPLETE } from '../../constants/api';
import { useDebounce } from '../../hooks/useDebounce';
import { ClaimsInquiryDetails } from '../ClaimsInquiryDetails';
import { handleAOAutocompleteOnBlur, handleAOAutocompleteOnChange, handleAOAutocompleteOnInputChange } from '../CommonUtilityServices/EventHandlerService';
import ClaimsDataGrid from './ClaimsDataGrid';
import OneStopClaimReporting from './OneStopClaimReporting';

const getTextFieldLabelAndPlaceholder = (searchByType) => {
  switch (searchByType) {
    case 'policyNumber':
      return { label: 'Policy Number', placeholder: 'Enter Policy Number' };
    case 'insuredName':
      return { label: 'Name', placeholder: 'Enter Insured Name' };
    case 'claimantName':
      return { label: 'Last Name', placeholder: 'Enter Claimant Last Name' };
  }
};

const ClaimsSearch = ({ claimsInquiry, setClaimsInquiry }) => {
  const [claimantNameSearchType, setClaimantNameSearchType] = useState('exact');
  const [searchByType, setSearchByType] = useState('policyNumber');
  const [autocompleteInputValues, setAutocompleteInputValues] = useState({
    policyNumber: { value: '', invalidText: '' },
    insuredName: { value: '', invalidText: '' },
    claimantName: { value: '', invalidText: '' },
  });
  const [formData, setFormData] = useState({
    policyNumber: '',
    insuredName: '',
    claimantName: '',
  });
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState(null);

  const [debouncedState, setDebouncedState] = useDebounce(autocompleteInputValues);
  const { results, loadingSearchResults, error, getAutocompleteEndpoint, handleSearchOnClick, handleClickPaginationChange, resetState } = useClaims();

  useEffect(() => {
    const getSuggestions = async () => {
      const fetchSuggestions = async (endpoint, searchText) => {
        const claimsEndpointUrl = `${POST_CLAIMS_AUTOCOMPLETE}/${endpoint}`;
        const claimsEndpointData = await axios.post(`${claimsEndpointUrl}`, searchText);
        const claimsEndpointSuggestion = claimsEndpointData.data;
        return claimsEndpointSuggestion;
      };

      if (autocompleteInputValues[searchByType].value.length >= 3) {
        const suggestions = await fetchSuggestions(getAutocompleteEndpoint(searchByType), autocompleteInputValues[searchByType].value);
        setSuggestionOptions(suggestions);
      } else {
        setSuggestionOptions([]);
      }
    };

    getSuggestions();
  }, [debouncedState[searchByType], searchByType]);

  useEffect(() => {
    const getResults = () => {
      if (claimsInquiry) {
        setFormData({ ...formData, policyNumber: claimsInquiry.policyNumber });
        handleSearchOnClick(searchByType, claimantNameSearchType, claimsInquiry.policyNumber);
        setClaimsInquiry(null);
      }
    };

    getResults();
  }, [claimsInquiry]);

  const { label, placeholder } = getTextFieldLabelAndPlaceholder(searchByType);

  const handleClaimantNameSearchTypeOnChange = (e) => setClaimantNameSearchType(e.target.value);

  const handleSearchByTypeOnChange = (e) => {
    setSuggestionOptions([]);
    resetState();
    setSearchByType(e.target.value);
  };

  const handleSearchTextOnChange = (e, value) => handleAOAutocompleteOnChange(searchByType, e, value, formData, setFormData);

  const handleSearchTextInputValueOnChange = (e, value) =>
    handleAOAutocompleteOnInputChange(searchByType, e, value, autocompleteInputValues, setAutocompleteInputValues, debouncedState, setDebouncedState);

  const handleOnBlur = () => handleAOAutocompleteOnBlur(searchByType, formData, setFormData, autocompleteInputValues);

  const handleSearch = () => handleSearchOnClick(searchByType, claimantNameSearchType, autocompleteInputValues[searchByType].value);

  const handleOnPageChange = (page) => handleClickPaginationChange(page, searchByType, claimantNameSearchType, autocompleteInputValues[searchByType].value);

  const handleCloseClaimsInquiryDetails = () => setSelectedClaim(null);

  const isSearchButtonDisabled = () => {
    switch (searchByType) {
      case 'policyNumber':
        return autocompleteInputValues[searchByType].value.length < 6;
      case 'insuredName':
      case 'claimantName':
        return autocompleteInputValues[searchByType].value.length < 3;
    }
  };

  return (
    <>
      <Typography variant="h5" style={{ marginBottom: 16 }}>
        Claims Inquiry Search
      </Typography>

      <Row spacing={2}>
        <Col xs={8}>
          <Paper>
            <Typography id="ClaimsTypographySearchCriteria" variant="subtitle1">
              Search Criteria:
            </Typography>

            <FormControl component="fieldset" style={{ marginBottom: 16 }}>
              <RadioGroup aria-label="claims-searchtype" name="claims-searchtype" row value={searchByType} onChange={handleSearchByTypeOnChange}>
                <Radio id="claims-insured-policy-number-radio" value="policyNumber" label="Policy Number" />
                <Radio id="claims-policy-insured-name-radio" value="insuredName" label="Insured Name" />
                <Radio id="claims-quote-claimant-name-radio" value="claimantName" label="Claimant Name" />
              </RadioGroup>
            </FormControl>

            {searchByType === 'policyNumber' && (
              <Typography color="textSecondary" style={{ marginBottom: 16 }}>
                Please enter the policy number then click on search to retrieve the information.
              </Typography>
            )}

            <Row alignItems="center" spacing={2}>
              <Col>
                <Autocomplete
                  id="claims-search-input"
                  disableClearable
                  filterOptions={(options) => options}
                  value={formData[searchByType]}
                  inputValue={formData[searchByType].value}
                  options={suggestionOptions ?? []}
                  onChange={handleSearchTextOnChange}
                  onInputChange={handleSearchTextInputValueOnChange}
                  onBlur={handleOnBlur}
                  style={{ width: 300 }}
                  renderInput={(params) => <TextField {...params} required label={label} placeholder={placeholder} />}
                />
              </Col>
              <Col>
                <Button disabled={isSearchButtonDisabled() || loadingSearchResults} loading={loadingSearchResults} onClick={handleSearch}>
                  Search
                </Button>
              </Col>
            </Row>

            {searchByType === 'claimantName' && (
              <FormControl id="ClaimsSearchRadioButtonSection" component="fieldset" style={{ marginTop: 8 }}>
                <RadioGroup
                  aria-label="claims-searchtype"
                  name="claims-searchtype"
                  row
                  value={claimantNameSearchType}
                  onChange={handleClaimantNameSearchTypeOnChange}>
                  <Radio id="claims-policy-insured-name-exact-radio" value="exact" label="Exact" />
                  <Radio id="claims-policy-insured-name-contains-radio" value="startswith" label="Starts With " />
                </RadioGroup>
              </FormControl>
            )}
          </Paper>
        </Col>
        <Col xs={4}>
          <OneStopClaimReporting />
        </Col>
      </Row>

      {error && <Alert severity="error" message={error} />}

      <ClaimsDataGrid
        results={results}
        setSelectedClaim={setSelectedClaim}
        loadingSearchResults={loadingSearchResults}
        handleOnPageChange={handleOnPageChange}
        searchByType={searchByType}
      />

      {selectedClaim && <ClaimsInquiryDetails selectedClaim={selectedClaim} handleCloseClaimsInquiryDetails={handleCloseClaimsInquiryDetails} />}
    </>
  );
};

ClaimsSearch.propTypes = {
  claimsInquiry: PropTypes.object,
  setClaimsInquiry: PropTypes.func,
};

export default ClaimsSearch;
